import React, { useContext, useState, useEffect } from 'react';
import { renderRoutes } from 'react-router-config';
import { FuseScrollbars, FuseMessage, FuseDialog, FuseSuspense } from '@fuse';
import { makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';
import ToolbarLayout from './components/ToolbarLayout';
import FooterLayout from './components/FooterLayout';
import NavbarWrapperLayout from './components/NavbarWrapperLayout';
import CopilotChatWidget from '../shared-components/CopilotChatWidget';
// Feature: 001-org-multi-tenancy - License Warning Banner (T045)
import LicenseWarningBanner from '../shared-components/LicenseWarningBanner';
import clsx from 'clsx';
import AppContext from 'app/AppContext';

const useStyles = makeStyles(theme => ({
	root: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		height: '100%',
		overflow: 'hidden',
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
		'&.boxed': {
			maxWidth: 1280,
			margin: '0 auto',
			boxShadow: theme.shadows[3]
		},
		'&.scroll-body': {
			'& $wrapper': {
				height: 'auto',
				flex: '0 0 auto',
				overflow: 'auto'
			},
			'& $contentWrapper': {},
			'& $content': {}
		},
		'&.scroll-content': {
			'& $wrapper': {},
			'& $contentWrapper': {},
			'& $content': {}
		},
		'& .navigation': {
			'& .list-subheader-text, & .list-item-text, & .item-badge, & .arrow-icon': {
				transition: theme.transitions.create('opacity', {
					duration: theme.transitions.duration.shortest,
					easing: theme.transitions.easing.easeInOut
				})
			}
		}
	},
	wrapper: {
		display: 'flex',
		position: 'relative',
		width: '100%',
		height: '100%',
		flex: '1 1 auto'
	},
	contentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		zIndex: 3,
		overflow: 'hidden',
		flex: '1 1 auto',
		'&::before': {
			content: '""',
			position: 'absolute',
			left: 0,
			right: 0,
			top: 0,
			height: 311,
			backgroundColor: theme.palette.background.default,
			backgroundSize: 'cover',
			pointerEvents: 'none',
			zIndex: 0,
			transition: 'opacity 0.3s ease-in-out',
			opacity: 0
		},
		'&.loaded::before': {
			backgroundImage: 'url("assets/images/backgrounds/dark-material-bg.jpg")',
			opacity: 1
		}
	},
	content: {
		position: 'relative',
		display: 'flex',
		overflow: 'auto',
		flex: '1 1 auto',
		flexDirection: 'column',
		width: '100%',
		'-webkit-overflow-scrolling': 'touch',
		zIndex: 2
	}
}));

const Layout = props => {
	const socket = props.socket;
	const [contentLoaded, setContentLoaded] = useState(false);

	const config = useSelector(({ fuse }) => fuse.settings.current.layout.config);

	const appContext = useContext(AppContext);
	const classes = useStyles(props);
	const { routes } = appContext;

	useEffect(() => {
		// Add a small delay to ensure content has started rendering
		const timer = setTimeout(() => {
			setContentLoaded(true);
		}, 100);
		return () => clearTimeout(timer);
	}, []);

	return (
		<div id="fuse-layout" className={clsx(classes.root, config.mode, 'scroll-' + config.scroll)}>
			<div className="flex flex-1 flex-col overflow-hidden relative">
				<div className={classes.wrapper}>
					<NavbarWrapperLayout />
					<div className={clsx(classes.contentWrapper, contentLoaded && 'loaded')}>
						<ToolbarLayout socket={socket} />
						{/* Feature: 001-org-multi-tenancy - License Warning Banner (T045) */}
						<LicenseWarningBanner />
						<FuseScrollbars className={classes.content} scrollToTopOnRouteChange>
							<FuseDialog />
							<FuseSuspense>{renderRoutes(routes)}</FuseSuspense>
							{props.children}
						</FuseScrollbars>
						<FooterLayout />
					</div>
				</div>
			</div>
			<FuseMessage />
			<CopilotChatWidget />
		</div>
	);
};

export default Layout;
